import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { OtpStep } from "@/components/OtpStep";

export default function Login() {
  const { user, login, verifyOtp, resendOtp, error, setError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res && res.two_factor_required) { setChallenge(res); return; }
    if (res) navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-stretch">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1607414851776-f2fcc379fb48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/30" />
        <div className="relative flex items-center gap-3 text-white">
          <img src="/logo.png" alt="Al Arabia — Arabian Contracting Services" className="h-20 w-auto object-contain" data-testid="login-logo" />
        </div>
        <div className="relative text-white max-w-md">
          <h2 className="font-display text-4xl font-bold leading-tight tracking-tight">
            Accounting-grade payment vouchers for the UAE.
          </h2>
          <p className="mt-4 text-slate-300 text-sm leading-relaxed">
            Create, approve and pay AED vouchers with automatic 5% VAT, TRN-ready PDF exports and a
            tamper-proof audit trail. Paid vouchers lock automatically.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <img src="/logo.png" alt="Al Arabia — Arabian Contracting Services" className="h-16 w-auto object-contain" />
          </div>
          <h1 className="font-display text-2xl font-bold text-white tracking-tight">{challenge ? "Verify it's you" : "Sign in"}</h1>
          <p className="text-sm text-slate-400 mt-1 mb-6">{challenge ? "Two-factor verification" : "Access your voucher workspace."}</p>

          {challenge ? (
            <OtpStep challenge={challenge} error={error}
              onVerify={(code) => verifyOtp(challenge.challenge_id, code)}
              onResend={() => resendOtp(challenge.challenge_id)}
              onBack={() => { setChallenge(null); setPassword(""); setError(""); }} />
          ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.ae"
                className="mt-1.5 bg-slate-900 border-slate-800 text-white"
                data-testid="login-email"
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 bg-slate-900 border-slate-800 text-white"
                data-testid="login-password"
                required
              />
            </div>
            {error && (
              <div className="text-sm text-rose-400 bg-rose-950/40 border border-rose-900 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
